import type { ReactNode } from 'react';
import { money, dCell } from './format';
import { BankMark } from './BankMark';
import './dashboard.css';

/* ---------------- helpers ---------------- */
export function compact(n: number): string {
  if (n >= 1_000_000) return `₺${(n / 1_000_000).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}M`;
  if (n >= 1_000) return `₺${(n / 1_000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}B`;
  return money(n);
}

const DONUT_COLORS = ['#2563eb', '#38bdf8', '#16a34a', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6'];

/* ---------------- icons ---------------- */
const PATHS: Record<string, ReactNode> = {
  card: <><rect x="2" y="5" width="20" height="14" rx="2.5" /><path d="M2 10h20M6 15h4" /></>,
  check: <><circle cx="12" cy="12" r="9" /><path d="m8.5 12 2.5 2.5 4.5-5" /></>,
  percent: <path d="M5 19 19 5M8.5 8.5a2 2 0 1 0 0-.01M15.5 15.5a2 2 0 1 0 0-.01" />,
  users: <><circle cx="9" cy="8" r="3.2" /><path d="M3.5 20a5.5 5.5 0 0 1 11 0M17 11a3 3 0 0 0 0-6M20.5 20a5.5 5.5 0 0 0-4-5.3" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  bank: <path d="M4 10 12 4l8 6M6 10v9h12v-9M9 19v-5h6v5" />,
  plus: <path d="M12 5v14M5 12h14" />,
  up: <path d="m5 15 5-5 4 4 5-6" />,
};
export function Ico({ name, w = 20 }: { name: string; w?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={w} height={w} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {PATHS[name]}
    </svg>
  );
}

/* ---------------- hero ---------------- */
function smoothPath(pts: Array<[number, number]>): string {
  if (pts.length < 2) return '';
  let d = `M ${pts[0][0]},${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const [x0, y0] = pts[i];
    const [x1, y1] = pts[i + 1];
    const mx = (x0 + x1) / 2;
    d += ` C ${mx},${y0} ${mx},${y1} ${x1},${y1}`;
  }
  return d;
}
function areaPoints(values: number[], w: number, h: number, pad = 16): Array<[number, number]> {
  const n = values.length;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  return values.map((v, i) => [
    (i / Math.max(1, n - 1)) * w,
    h - pad - ((v - min) / span) * (h - pad * 2),
  ]);
}

export function Hero({
  eyebrow, amount, delta, weekly, minis,
}: {
  eyebrow: string; amount: number; delta: number | null;
  weekly: number[]; minis: Array<{ label: string; value: string }>;
}) {
  const W = 340, H = 150;
  const pts = areaPoints(weekly.length ? weekly : [0, 0], W, H);
  const line = smoothPath(pts);
  const area = pts.length ? `${line} L ${pts[pts.length - 1][0]},${H} L ${pts[0][0]},${H} Z` : '';
  return (
    <div className="dash-hero">
      <div>
        <div className="ey"><Ico name="card" w={15} />{eyebrow.toLocaleUpperCase('tr-TR')}</div>
        <div className="big">{money(amount)}</div>
        {delta != null && (
          <span className="dl"><Ico name="up" w={13} />Son 30 günde %{Math.abs(delta).toLocaleString('tr-TR')} {delta >= 0 ? 'artış' : 'azalış'}</span>
        )}
        <div className="dash-hmini">
          {minis.map((m) => (
            <div key={m.label}><span>{m.label}</span><b>{m.value}</b></div>
          ))}
        </div>
      </div>
      <div className="dash-hchart">
        <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
          <defs><linearGradient id="heroG" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#fff" stopOpacity=".4" /><stop offset="1" stopColor="#fff" stopOpacity="0" /></linearGradient></defs>
          {area && <path d={area} fill="url(#heroG)" />}
          {line && <path d={line} fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" />}
        </svg>
      </div>
    </div>
  );
}

/* ---------------- stat tile ---------------- */
export function StatTile({ tone, icon, value, label, delta }: {
  tone: 'g' | 'b' | 'o' | 'r'; icon: string; value: string; label: string; delta?: number | null;
}) {
  return (
    <div className="dash-stat">
      <div className={`ico ${tone}`}><Ico name={icon} /></div>
      <div><div className="val">{value}</div><div className="lbl">{label}</div></div>
      {delta != null && <div className={`dd ${delta >= 0 ? 'up' : 'down'}`}>{delta >= 0 ? '↑' : '↓'} %{Math.abs(delta).toLocaleString('tr-TR')}</div>}
    </div>
  );
}

/* ---------------- bar chart ---------------- */
export function BarChart({ data, note }: { data: Array<{ label: string; value: number }>; note?: string }) {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="dash-card">
      <div className="dash-ch"><div><h3>Haftalık Tahsilat</h3><div className="dash-sub">Son 7 gün · ₺</div></div>{note && <div className="dash-tag">{note}</div>}</div>
      <div className="dash-bars">
        {data.map((d, i) => (
          <div className="dash-bar" key={i}>
            <div className="col" style={{ height: `${Math.max(3, (d.value / max) * 100)}%` }} title={money(d.value)} />
            <div className="bl">{d.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- donut ---------------- */
export function Donut({ items, total }: { items: Array<{ label: string; pct: number }>; total: number }) {
  let cum = 0;
  return (
    <div className="dash-card">
      <div className="dash-ch"><div><h3>Banka Dağılımı</h3><div className="dash-sub">Onaylı işlemler</div></div></div>
      {items.length === 0 ? (
        <div className="dash-empty">Henüz onaylı işlem yok.</div>
      ) : (
        <div className="dash-donutwrap">
          <div className="dash-donut">
            <svg width="140" height="140" viewBox="0 0 42 42">
              <circle cx="21" cy="21" r="15.9" fill="none" stroke="var(--line)" strokeWidth="6" />
              {items.map((it, i) => {
                const off = 25 - cum;
                cum += it.pct;
                return (
                  <circle key={i} cx="21" cy="21" r="15.9" fill="none" stroke={DONUT_COLORS[i % DONUT_COLORS.length]}
                    strokeWidth="6" strokeDasharray={`${it.pct} ${100 - it.pct}`} strokeDashoffset={off} strokeLinecap="round" />
                );
              })}
            </svg>
            <div className="mid"><b>{total}</b><span>işlem</span></div>
          </div>
          <div className="dash-legend">
            {items.map((it, i) => (
              <div className="dash-lg" key={i}>
                <span className="sw" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                <span className="nm">{it.label}</span><span className="pc">%{it.pct}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- recent table ---------------- */
const PILL: Record<string, { cls: string; label: string }> = {
  approved: { cls: 'ok', label: 'Onaylandı' },
  pending: { cls: 'wait', label: 'Bekliyor' },
  failed: { cls: 'fail', label: 'Başarısız' },
};

interface RecentRow {
  order_id: string; amount: string; bank_key: string | null; status: string; created_at: string;
  user_company?: string | null; user_name?: string | null;
}

export function RecentTable({ rows, showCustomer, onRow }: {
  rows: RecentRow[]; showCustomer: boolean; onRow?: (orderId: string) => void;
}) {
  return (
    <div className="dash-card">
      <div className="dash-ch"><div><h3>Son İşlemler</h3><div className="dash-sub">En güncel {rows.length} tahsilat</div></div></div>
      {rows.length === 0 ? (
        <div className="dash-empty">Henüz işlem yok.</div>
      ) : (
        <table className="dash-table">
          <thead><tr>{showCustomer && <th>Bayi</th>}{!showCustomer && <th>Sipariş</th>}<th>Banka</th><th>Tutar</th><th>Durum</th><th>Tarih</th></tr></thead>
          <tbody>
            {rows.map((r) => {
              const p = PILL[r.status] ?? PILL.pending;
              const name = r.user_company || r.user_name || 'Bayi';
              const dt = dCell(r.created_at);
              return (
                <tr key={r.order_id} style={onRow ? { cursor: 'pointer' } : undefined} onClick={onRow ? () => onRow(r.order_id) : undefined}>
                  {showCustomer ? (
                    <td><div className="cust"><div className="ci">{name.slice(0, 2).toUpperCase()}</div><div><b>{name}</b><span>#{r.order_id.slice(0, 10)}…</span></div></div></td>
                  ) : (
                    <td><span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>#{r.order_id.slice(0, 14)}…</span></td>
                  )}
                  <td><BankMark bankKey={r.bank_key} /></td>
                  <td className="dash-amt">{money(Number(r.amount))}</td>
                  <td><span className={`dash-pill ${p.cls}`}>{p.label}</span></td>
                  <td className="dash-tdate"><b>{dt.date}</b><span>{dt.time}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function DashSkeleton() {
  return (
    <div className="dash">
      <div className="dash-skel" style={{ height: 168 }} />
      <div className="dash-stats"><div className="dash-skel" style={{ height: 78 }} /><div className="dash-skel" style={{ height: 78 }} /><div className="dash-skel" style={{ height: 78 }} /></div>
      <div className="dash-grid"><div className="dash-skel" style={{ height: 220 }} /><div className="dash-skel" style={{ height: 220 }} /></div>
      <div className="dash-skel" style={{ height: 260 }} />
    </div>
  );
}
